import { getHashPath } from '@/lib/hash-route';

export const BUG_REPORT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const MAX_PIXELS = 25_000_000;
const MAX_EDGE = 2560;

export type BugReportPriority = 'low' | 'medium' | 'high' | 'urgent';

export const BUG_REPORT_PRIORITIES: { value: BugReportPriority; label: string }[] = [
  { value: 'low', label: 'Niedrig' },
  { value: 'medium', label: 'Mittel' },
  { value: 'high', label: 'Hoch' },
  { value: 'urgent', label: 'Dringend' },
];

/** Current page link: keeps the hash route, drops query strings (may carry tokens or invite codes). */
export function currentReportUrl(): string {
  return `${window.location.origin}${window.location.pathname}#${getHashPath()}`;
}

export function newRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Accepts PNG/JPG/WebP as-is when within limits; otherwise re-encodes to WebP
 * (max 2560 px edge) so large clipboard screenshots still fit the 5 MB limit.
 */
export async function prepareBugReportImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) throw new Error('Nur Bilder (PNG, JPG oder WebP) sind erlaubt.');

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Bild konnte nicht gelesen werden.');
  }

  try {
    const pixels = bitmap.width * bitmap.height;
    if (ALLOWED_TYPES.has(file.type) && file.size <= BUG_REPORT_MAX_IMAGE_BYTES && pixels <= MAX_PIXELS) {
      return file;
    }

    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Bild konnte nicht verarbeitet werden.');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
    if (!blob || blob.type !== 'image/webp') throw new Error('Bild konnte nicht verarbeitet werden.');
    if (blob.size > BUG_REPORT_MAX_IMAGE_BYTES) throw new Error('Bild ist auch nach dem Verkleinern grösser als 5 MB.');

    const base = (file.name || 'screenshot').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.webp`, { type: 'image/webp' });
  } finally {
    bitmap.close();
  }
}
